import React from 'react';
import { Box, Card, CardContent, Typography, useTheme, Chip, Button } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const YieldForecaster: React.FC = () => {
  const theme = useTheme();

  const data = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Historical Yield (Tons)',
        data: [12, 14, 13, 15, 18, 22, 24, 21, 19, 16, 14, 13],
        borderColor: theme.palette.grey[400],
        backgroundColor: 'transparent',
        borderWidth: 2,
        tension: 0.4,
        borderDash: [5, 5],
        pointRadius: 0,
      },
      {
        label: 'AI Predicted Yield (Tons)',
        data: [12, 14, 13, 16, 20, 25, 28, 25, 22, 19, 17, 15],
        borderColor: theme.palette.success.main,
        backgroundColor: `${theme.palette.success.main}20`,
        borderWidth: 3,
        tension: 0.4,
        fill: true,
        pointBackgroundColor: theme.palette.success.main,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          usePointStyle: true,
        },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: theme.palette.divider,
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false,
    },
  };

  return (
    <Card sx={{ height: '100%', borderRadius: 3, boxShadow: theme.shadows[2] }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center" gap={1}>
              <AutoAwesomeIcon color="secondary" />
              AI Yield Forecast
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Predicted vs Historical Performance
            </Typography>
          </Box>
          <Chip 
            icon={<TrendingUpIcon />} 
            label="+18% Projected Growth" 
            color="success" 
            sx={{ bgcolor: `${theme.palette.success.main}20`, color: theme.palette.success.dark, fontWeight: 'bold' }}
          />
        </Box>

        <Box sx={{ height: 300 }}>
          <Line data={data} options={options} />
        </Box>

        <Box mt={2} p={2} bgcolor={theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'} borderRadius={2}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            AI Insight
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Based on current soil moisture and weather patterns, we predict a bumper harvest in July. 
            Recommendation: Increase potassium intake by 5% in the next fertigation cycle to maximize fruit size.
          </Typography>
          <Button size="small" sx={{ mt: 1 }} color="secondary">
            View Detailed Analysis
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default YieldForecaster;
